/**
 * Throttle state — per-family send bookkeeping the live-wire send path
 * reads through `checkLimit` before dispatching. Two buckets:
 *
 *   - per-tick: resets every time the world-model tick advances. The
 *     bootstrap subscribes to `useWorldModel` and calls `resetTick` on
 *     each new tick boundary.
 *   - per-minute: a rolling array of wall-clock timestamps per family,
 *     pruned on each `recordSend`.
 *
 * Pre-Active gate (Q-C16 / P1.8 C2): until the first `resetTick` fires —
 * which happens when the initial `SnapshotMessage` carries the first
 * server tick through `useWorldModel` — the `bootstrapped` flag is
 * `false` and `checkLimit` skips tick-scoped caps. Per-minute caps still
 * apply (they're wall-clock anchored). The server authoritatively
 * rejects pre-connection intents (P1.4 Disconnected-state filter), so
 * the client's advisory throttle being permissive in that window is
 * safe + avoids tripping users on rapid pre-Active UI interactions.
 */

import { create } from 'zustand';

import {
  familyPlane,
  type IntentFamily,
  type ThrottleSnapshot,
} from './policy';

const MINUTE_WINDOW_MS = 60_000;

export interface ThrottleState {
  perTick: Partial<Record<IntentFamily, number>>;
  worldIntentThisTick: number;
  perMinuteTimestamps: Partial<Record<IntentFamily, number[]>>;
  currentTick: bigint;
  /** False until the first `resetTick` fires (Active-state marker). */
  bootstrapped: boolean;

  // ─── Actions ─────────────────────────────────────────────────────
  snapshot: () => ThrottleSnapshot;
  recordSend: (family: IntentFamily, nowMs: number) => void;
  /** Reset per-tick buckets + set the new currentTick + flip bootstrapped
   *  to true on first call. */
  resetTick: (tick: bigint) => void;
  /** Full reset (logout / test). */
  reset: () => void;
}

export const useThrottle = create<ThrottleState>((set, get) => ({
  perTick: {},
  worldIntentThisTick: 0,
  perMinuteTimestamps: {},
  currentTick: 0n,
  bootstrapped: false,

  snapshot: () => {
    const s = get();
    return {
      perTick: s.perTick,
      worldIntentThisTick: s.worldIntentThisTick,
      perMinuteTimestamps: s.perMinuteTimestamps,
      bootstrapped: s.bootstrapped,
    };
  },

  recordSend: (family, nowMs) =>
    set((s) => {
      const perTick = { ...s.perTick };
      perTick[family] = (perTick[family] ?? 0) + 1;
      const isWorld = familyPlane(family) === 'World';
      const worldIntentThisTick = isWorld
        ? s.worldIntentThisTick + 1
        : s.worldIntentThisTick;

      const perMinuteTimestamps = { ...s.perMinuteTimestamps };
      const pruned = (perMinuteTimestamps[family] ?? []).filter(
        (t) => nowMs - t <= MINUTE_WINDOW_MS,
      );
      pruned.push(nowMs);
      perMinuteTimestamps[family] = pruned;

      return { perTick, worldIntentThisTick, perMinuteTimestamps };
    }),

  resetTick: (tick) =>
    set(() => ({
      perTick: {},
      worldIntentThisTick: 0,
      currentTick: tick,
      bootstrapped: true,
    })),

  reset: () =>
    set(() => ({
      perTick: {},
      worldIntentThisTick: 0,
      perMinuteTimestamps: {},
      currentTick: 0n,
      bootstrapped: false,
    })),
}));
